/**
 * Shared default-view logic for the two geofence maps.
 *
 * Both maps opened on a hardcoded Sydney centre regardless of the selected
 * venue. They now frame whatever geofences they have loaded and fall back to a
 * country-wide view when there are none, so the opening view is always derived
 * from data rather than assumed.
 */

export type LatLng = { lat: number; lng: number };

/** Fallback view, used whenever there are no usable fence coordinates to frame. */
export const AUSTRALIA_VIEW: { center: LatLng; zoom: number } = {
  center: { lat: -25.2744, lng: 133.7751 },
  zoom: 4,
};

/**
 * A fence usually traces a single building, so fitting the bounds to one would
 * zoom to the maximum and leave no surrounding context to orient by.
 */
const MAX_FIT_ZOOM = 16;

const FIT_PADDING_PX = 48;

/**
 * Fences saved without a real position come back as 0,0. A single one of those
 * stretches the bounds into the Atlantic and zooms the map out to the globe.
 */
export const isUsableCoord = (coord: LatLng): boolean => {
  if (!coord) return false;

  const { lat, lng } = coord;

  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!isFinite(lat) || !isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;

  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/** True when the fence has at least three real points, i.e. it can be drawn. */
export const hasUsableCoordinates = (fence: { coordinates: LatLng[] }): boolean =>
  (fence.coordinates || []).filter(isUsableCoord).length >= 3;

export const collectFenceCoordinates = (
  fences: { coordinates: LatLng[] }[]
): LatLng[] =>
  fences.reduce<LatLng[]>(
    (all, fence) => all.concat(fence.coordinates || []),
    []
  );

/**
 * Centre `map` on the supplied coordinates, deriving zoom from their bounds, or
 * drop back to AUSTRALIA_VIEW when none of them are usable.
 */
export const fitMapToCoordinates = (map: any, coordinates: LatLng[]): void => {
  if (!map) return;

  const usable = (coordinates || []).filter(isUsableCoord);

  if (usable.length === 0) {
    map.setCenter(AUSTRALIA_VIEW.center);
    map.setZoom(AUSTRALIA_VIEW.zoom);
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  usable.forEach((coord) => bounds.extend(coord));

  map.fitBounds(bounds, FIT_PADDING_PX);

  // fitBounds settles asynchronously, so the cap can only be applied once the
  // map has actually taken the new zoom.
  google.maps.event.addListenerOnce(map, "idle", () => {
    const zoom = map.getZoom();
    if (typeof zoom === "number" && zoom > MAX_FIT_ZOOM) {
      map.setZoom(MAX_FIT_ZOOM);
    }
  });
};
